import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrintIcon from "@mui/icons-material/Print";
import SettingsIcon from "@mui/icons-material/Settings";
import GitHubIcon from "@mui/icons-material/GitHub";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ComputerIcon from "@mui/icons-material/Computer";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TuneIcon from "@mui/icons-material/Tune";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import PageHeaderLight from "../../components/ui/PageHeaderLight";

const GITHUB_RELEASES_URL =
  "https://github.com/slapie-delivery/printer-releases/releases";
const GITHUB_API_URL =
  "https://api.github.com/repos/slapie-delivery/printer-releases/releases/latest";

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

export default function PrintersHelpScreen() {
  const theme = useTheme();
  const [latestRelease, setLatestRelease] = useState(null);
  const [isFetchingRelease, setIsFetchingRelease] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    fetchLatestRelease();
  }, []);

  const fetchLatestRelease = async () => {
    setIsFetchingRelease(true);

    try {
      const response = await fetch(GITHUB_API_URL);

      const hasValidResponse = response.ok;
      if (!hasValidResponse) {
        throw new Error("Failed to fetch release");
      }

      const data = await response.json();

      const nsisAsset = data.assets?.find(
        (asset) =>
          asset.name.endsWith(".exe") && !asset.name.includes("portable")
      );

      const portableAsset = data.assets?.find(
        (asset) =>
          asset.name.includes("portable") && asset.name.endsWith(".exe")
      );

      setLatestRelease({
        version: data.tag_name,
        releaseUrl: data.html_url,
        downloadUrl: nsisAsset?.browser_download_url || null,
        portableUrl: portableAsset?.browser_download_url || null,
        publishedAt: data.published_at,
      });
    } catch (error) {
      console.error("Error fetching latest release:", error);
      setLatestRelease(null);
    } finally {
      setIsFetchingRelease(false);
    }
  };

  const handleDownload = (url) => {
    const hasValidUrl = url && url.length > 0;
    if (!hasValidUrl) {
      window.open(GITHUB_RELEASES_URL, "_blank");
      return;
    }

    window.open(url, "_blank");
  };

  const handleOpenApp = () => {
    try {
      window.open("slapie://open", "_blank");
    } catch (error) {
      console.error("Erro ao abrir o app:", error);
      alert(
        "Não foi possível abrir o app automaticamente. Por favor, abra manualmente o aplicativo Slapie no Windows."
      );
    }
  };

  const handleOpenReleases = () => {
    window.open(GITHUB_RELEASES_URL, "_blank");
  };

  const renderStepIcon = (step, index) => {
    const isStepCompleted = index <= activeStep;
    const backgroundColor = isStepCompleted
      ? theme.palette.primary.main
      : theme.palette.grey[300];

    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor,
          color: theme.palette.common.white,
        }}
      >
        {step.icon}
      </Box>
    );
  };

  const renderStepLabel = (step, index) => {
    const isLastStep = index === installationSteps.length - 1;
    const optionalLabel = isLastStep ? (
      <Typography variant="caption">Última etapa</Typography>
    ) : null;

    return (
      <StepLabel
        optional={optionalLabel}
        StepIconComponent={() => renderStepIcon(step, index)}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {step.label}
        </Typography>
      </StepLabel>
    );
  };

  const renderStepContent = (step, index) => {
    const isLastStep = index === installationSteps.length - 1;

    return (
      <StepContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {step.description}
        </Typography>
        {!isLastStep && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setActiveStep(index + 1)}
          >
            Próximo
          </Button>
        )}
      </StepContent>
    );
  };

  const installationSteps = [
    {
      label: "Baixar o Aplicativo",
      description:
        "Faça o download do instalador ou da versão portátil do aplicativo Slapie.",
      icon: <CloudDownloadIcon />,
    },
    {
      label: "Executar o Instalador",
      description:
        "Execute o arquivo baixado e siga as instruções na tela. O processo é rápido e simples.",
      icon: <PlayArrowIcon />,
    },
    {
      label: "Abrir o Aplicativo",
      description:
        "Após a instalação, abra o aplicativo Slapie. Ele iniciará automaticamente na barra de tarefas.",
      icon: <SettingsIcon />,
    },
    {
      label: "Configurar Impressora",
      description:
        "O aplicativo detectará automaticamente as impressoras disponíveis no sistema.",
      icon: <PrintIcon />,
    },
    {
      label: "Pronto para Usar",
      description:
        "Agora você pode imprimir diretamente do sistema web sem precisar gerar PDFs.",
      icon: <CheckCircleIcon />,
    },
  ];

  const paperSizes = [
    {
      size: "80mm",
      width: "80mm",
      height: "Contínuo",
      usage: "Impressoras térmicas padrão",
    },
    {
      size: "58mm",
      width: "58mm",
      height: "Contínuo",
      usage: "Impressoras térmicas compactas",
    },
    {
      size: "A4",
      width: "210mm",
      height: "297mm",
      usage: "Impressoras convencionais",
    },
  ];

  const faqs = [
    {
      question: "Preciso deixar o aplicativo aberto o tempo todo?",
      answer:
        "Sim, o aplicativo precisa estar em execução para processar as impressões. Ele ficará minimizado na barra de tarefas e não atrapalha seu trabalho.",
    },
    {
      question: "O aplicativo funciona em outros sistemas operacionais?",
      answer:
        "Atualmente, o aplicativo é compatível apenas com Windows. Versões para outros sistemas estão em desenvolvimento.",
    },
    {
      question: "Como atualizo o aplicativo?",
      answer:
        "O aplicativo verifica atualizações automaticamente. Quando uma nova versão estiver disponível, você será notificado.",
    },
    {
      question: "O que fazer se a impressora não for detectada?",
      answer:
        "Verifique se a impressora está corretamente instalada no Windows e ligada. Reinicie o aplicativo Slapie e tente novamente.",
    },
    {
      question: "É seguro usar o aplicativo?",
      answer: "Sim, o aplicativo é desenvolvido e mantido pela equipe Slapie.",
    },
  ];

  const renderWelcomeSection = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        mb: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <motion.div
          animate={{
            rotate: [0, 5, -5, 0],
            transition: { duration: 2, repeat: Infinity },
          }}
        >
          <PrintIcon
            sx={{
              fontSize: 48,
              color: theme.palette.primary.main,
            }}
          />
        </motion.div>
        <Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Bem-vindo ao Sistema de Impressão Slapie
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Imprima diretamente do navegador sem precisar gerar PDFs
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="body1" paragraph>
        O aplicativo Slapie permite que você imprima pedidos e documentos
        diretamente das páginas web, enviando os dados para sua impressora
        térmica ou convencional sem a necessidade de gerar arquivos PDF.
      </Typography>

      <Alert severity="info" sx={{ mt: 3 }} icon={<InfoOutlinedIcon />}>
        <AlertTitle>Requisitos do Sistema</AlertTitle>
        <Box component="ul" sx={{ mt: 1, mb: 0 }}>
          <li>
            <Typography variant="body2">Windows 10 ou superior</Typography>
          </li>
          <li>
            <Typography variant="body2">
              Impressora térmica ou convencional instalada
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Conexão com a internet para download inicial
            </Typography>
          </li>
        </Box>
      </Alert>
    </Paper>
  );

  const renderInstallationSection = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        mb: 4,
      }}
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <SettingsIcon />
        Instalação Passo a Passo
      </Typography>

      <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 3 }}>
        {installationSteps.map((step, index) => (
          <Step key={step.label} expanded>
            {renderStepLabel(step, index)}
            {renderStepContent(step, index)}
          </Step>
        ))}
      </Stepper>

      {activeStep === installationSteps.length && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success" icon={<CheckCircleIcon />}>
            Instalação concluída! Você está pronto para começar a imprimir.
          </Alert>
        </Box>
      )}
    </Paper>
  );

  const renderConfigurationSection = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        mb: 4,
      }}
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <TuneIcon />
        Configuração da Impressora
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mt: 2, mb: 3 }}>
        Configure sua impressora de acordo com o tipo de papel e necessidades de
        impressão.
      </Typography>

      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <AlertTitle>Como Acessar as Configurações</AlertTitle>
        <Box component="ol" sx={{ mt: 1, mb: 0, pl: 2 }}>
          <li>
            <Typography variant="body2">
              Abra o aplicativo Slapie (clique no ícone na barra de tarefas)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Clique no ícone de <strong>Impressoras</strong> (no menu lateral)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Selecione a impressora que deseja configurar
            </Typography>
          </li>
        </Box>
      </Alert>

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3 }}
        >
          <AspectRatioIcon />
          Tamanhos de Papel Suportados
        </Typography>

        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Tamanho</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Largura</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Altura</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Uso Recomendado
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paperSizes.map((paper) => (
                <TableRow key={paper.size}>
                  <TableCell>
                    <Chip
                      label={paper.size}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{paper.width}</TableCell>
                  <TableCell>{paper.height}</TableCell>
                  <TableCell>{paper.usage}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>Atenção</AlertTitle>
          Certifique-se de selecionar o tamanho correto do papel instalado em
          sua impressora para evitar problemas de formatação ou corte incorreto.
        </Alert>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <FormatListNumberedIcon />
          Quantidade de Cópias
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 2 }}>
          Configure quantas vias de cada pedido devem ser impressas
          automaticamente:
        </Typography>

        <Box sx={{ pl: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" color="primary">
              1 cópia (padrão)
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Ideal para operações simples onde apenas uma via é necessária
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" color="primary">
              2 cópias
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Recomendado quando você precisa de uma via para o entregador e
              outra para arquivo
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" color="primary">
              3 ou mais cópias
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Para operações que necessitam de múltiplas vias (cozinha, caixa,
              entregador, etc)
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" icon={<ReceiptIcon />} sx={{ mt: 2 }}>
          <AlertTitle>Dica de Economia</AlertTitle>
          Configure apenas as cópias realmente necessárias para economizar papel
          e reduzir custos. Você pode sempre imprimir manualmente uma via
          adicional quando necessário.
        </Alert>
      </Box>

      <Box>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <CheckCircleIcon />
          Melhores Práticas
        </Typography>

        <Box component="ul" sx={{ mt: 2, pl: 3 }}>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Teste antes de usar:</strong> Imprima um pedido de teste
              para verificar se a formatação está correta
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Verifique o papel:</strong> Sempre mantenha papel
              suficiente na impressora para evitar interrupções
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Mantenha atualizado:</strong> Atualize o aplicativo
              regularmente para ter acesso às melhorias e correções
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Impressora padrão:</strong> Defina a impressora térmica
              como padrão no Windows para facilitar o processo
            </Typography>
          </li>
        </Box>
      </Box>
    </Paper>
  );

  const renderFaqItem = (faq, index, totalFaqs) => {
    const isNotLastItem = index < totalFaqs - 1;

    return (
      <Box key={index} sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          color="primary"
          gutterBottom
        >
          {faq.question}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {faq.answer}
        </Typography>
        {isNotLastItem && <Divider sx={{ mt: 2 }} />}
      </Box>
    );
  };

  const renderFaqSection = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        mb: 4,
      }}
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <HelpOutlineIcon />
        Perguntas Frequentes
      </Typography>

      <Box sx={{ mt: 3 }}>
        {faqs.map((faq, index) => renderFaqItem(faq, index, faqs.length))}
      </Box>
    </Paper>
  );

  const renderTroubleshootingSection = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      }}
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <WarningAmberIcon />
        Resolução de Problemas
      </Typography>

      <Alert severity="warning" sx={{ mt: 3, mb: 2 }}>
        <AlertTitle>App não está respondendo?</AlertTitle>
        <Box component="ol" sx={{ mt: 1, mb: 0, pl: 2 }}>
          <li>
            <Typography variant="body2">
              Verifique se o app está aberto na barra de tarefas (canto inferior
              direito)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Tente fechar e abrir o aplicativo novamente
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Reinicie o computador se o problema persistir
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Verifique se há atualizações disponíveis
            </Typography>
          </li>
        </Box>
      </Alert>

      <Alert severity="info" icon={<InfoOutlinedIcon />}>
        <AlertTitle>Dica</AlertTitle>
        Pressione <Chip label="⊞ Win" size="small" /> + digite "Slapie" para
        localizar rapidamente o aplicativo no menu Iniciar.
      </Alert>
    </Paper>
  );

  const renderDownloadCard = () => {
    const hasRelease = latestRelease !== null;
    const hasDownloadUrl = hasRelease && latestRelease.downloadUrl;
    const hasPortableUrl = hasRelease && latestRelease.portableUrl;

    return (
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          position: "sticky",
          top: 24,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <GitHubIcon
              sx={{ fontSize: 40, color: theme.palette.primary.main }}
            />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Download
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Aplicativo Slapie
              </Typography>
            </Box>
          </Box>

          {isFetchingRelease && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!isFetchingRelease && hasRelease && (
            <>
              <Alert
                severity="success"
                sx={{ mb: 3 }}
                icon={<CheckCircleIcon />}
              >
                <AlertTitle>Versão Disponível</AlertTitle>
                <Typography variant="body2">
                  <strong>{latestRelease.version}</strong>
                </Typography>
              </Alert>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {hasDownloadUrl && (
                  <motion.div
                    whileHover="hover"
                    whileTap="tap"
                    variants={buttonVariants}
                  >
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(latestRelease.downloadUrl)}
                      sx={{ borderRadius: 2 }}
                    >
                      Baixar Instalador
                    </Button>
                  </motion.div>
                )}

                {hasPortableUrl && (
                  <motion.div
                    whileHover="hover"
                    whileTap="tap"
                    variants={buttonVariants}
                  >
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(latestRelease.portableUrl)}
                      sx={{ borderRadius: 2 }}
                    >
                      Baixar Versão Portátil
                    </Button>
                  </motion.div>
                )}

                <Divider sx={{ my: 1 }} />

                <motion.div
                  whileHover="hover"
                  whileTap="tap"
                  variants={buttonVariants}
                >
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    size="large"
                    startIcon={<OpenInNewIcon />}
                    onClick={handleOpenApp}
                    sx={{ borderRadius: 2 }}
                  >
                    Abrir App Slapie
                  </Button>
                </motion.div>

                <Button
                  variant="text"
                  fullWidth
                  startIcon={<GitHubIcon />}
                  onClick={handleOpenReleases}
                  sx={{ borderRadius: 2 }}
                >
                  Ver Todas as Versões
                </Button>
              </Box>
            </>
          )}

          {!isFetchingRelease && !hasRelease && (
            <Alert severity="error">
              <AlertTitle>Erro ao Carregar</AlertTitle>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Não foi possível carregar as informações de download.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleOpenReleases}
                startIcon={<GitHubIcon />}
              >
                Ir para GitHub
              </Button>
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <ComputerIcon fontSize="small" />
              Requisitos Mínimos
            </Typography>
            <Box component="ul" sx={{ mt: 2, pl: 2 }}>
              <li>
                <Typography variant="body2" color="textSecondary">
                  Windows 10 ou superior
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="textSecondary">
                  50 MB de espaço em disco
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="textSecondary">
                  Conexão com internet
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="textSecondary">
                  Impressora instalada
                </Typography>
              </li>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4, md: 6 }, maxWidth: "1800px", mx: "auto" }}>
      <PageHeaderLight
        title="Impressoras"
        subtitle="Como Configurar e Usar o Sistema de Impressão"
        actions={[]}
      />

      <Grid container spacing={4}>
        <Grid item xs={12} lg={8}>
          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
          >
            {renderWelcomeSection()}
          </motion.div>

          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.1 }}
          >
            {renderInstallationSection()}
          </motion.div>

          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
          >
            {renderConfigurationSection()}
          </motion.div>

          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.3 }}
          >
            {renderFaqSection()}
          </motion.div>

          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.4 }}
          >
            {renderTroubleshootingSection()}
          </motion.div>
        </Grid>

        <Grid item xs={12} lg={4}>
          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.1 }}
          >
            {renderDownloadCard()}
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
}
